import React, { memo, useMemo } from "react";
import { useQuery } from "react-query";
import { useRouteMatch } from "react-router-dom";

import Preloader from "~/components/Preloader";
import { EmployeeService } from "~/entity/Employee/api/service";
import { mergeEmployeeCardWithGaps } from "~/entity/Employee/lib/restGaps";
import { useGetEmployeeCardSupplementQuery } from "~/schemas/generated/graphql";

import EmployeeProfileView from "./EmployeeProfileView";
import {
  mapEmployeeCardToView,
  mapEmployeeCardToViewPositions,
} from "./EmployeeProfileView/model/mapCardToView";

const EmployeeProfileViewing = () => {
  const { params } = useRouteMatch<{ id: string }>();
  const personalId = params.id;

  const {
    data: cardResponse,
    isLoading: cardLoading,
    refetch: refetchCard,
  } = useQuery(
    ["employeeCard", personalId],
    () =>
      EmployeeService.GET.card(personalId).then((response) => response.data),
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
    },
  );

  const {
    data: supplement,
    loading: supplementLoading,
    refetch: refetchSupplement,
  } = useGetEmployeeCardSupplementQuery({
    variables: { id: personalId },
    fetchPolicy: "no-cache",
  });

  const merged = useMemo(() => {
    const card = cardResponse?.employee;
    if (!card) {
      return null;
    }
    return mergeEmployeeCardWithGaps(card, supplement);
  }, [cardResponse, supplement]);

  const employee = useMemo(
    () => (merged ? mapEmployeeCardToView(merged) : null),
    [merged],
  );
  const positions = useMemo(
    () => (merged ? mapEmployeeCardToViewPositions(merged) : []),
    [merged],
  );

  const isFired = useMemo(() => {
    const groups = merged?.positions || [];
    const hasFiredRow = groups.some((group) =>
      [...group.activePositions, ...group.inactivePositions].some(
        (row) =>
          row.actualTo == null &&
          (row.status === "fired" || row.status === "archive"),
      ),
    );
    const hasUnverifiedRow = groups.some((group) =>
      [...group.activePositions, ...group.inactivePositions].some(
        (row) => row.actualTo == null && row.status === "unverified",
      ),
    );
    return hasFiredRow && !hasUnverifiedRow;
  }, [merged]);

  const hasActivePosition = useMemo(
    () =>
      (merged?.positions || []).some((group) =>
        [...group.activePositions, ...group.inactivePositions].some(
          (row) => row.actualTo == null,
        ),
      ),
    [merged],
  );

  if (cardLoading || supplementLoading || !employee) {
    return <Preloader />;
  }

  return (
    <EmployeeProfileView
      employee={employee}
      employeeId={personalId}
      positions={positions}
      hasFiredPosition={isFired}
      hasActivePosition={hasActivePosition}
      refetch={async () => {
        const [cardResult] = await Promise.all([
          refetchCard(),
          refetchSupplement(),
        ]);
        return { isError: cardResult.isError || cardResult.isRefetchError };
      }}
    />
  );
};

export default memo(EmployeeProfileViewing);
